/**
 * MetadataTableCardView Component
 *
 * Mobile-optimized card view for displaying records.
 * Used instead of table on screens < 768px.
 */

import React from 'react'
import { List, Card, Empty, Skeleton, Typography, Tag, Badge } from 'antd'
import { RightOutlined } from '@ant-design/icons'
import type { FieldMetadata } from '@/services/metadataService'
import { formatCellValue, getStatusBadgeColor, isStatusField } from './utils/columnFormatters'
import type { MetadataTableCardViewProps } from './types'

const { Text } = Typography

/**
 * Render a field value for card display
 */
function renderCardValue(
  value: unknown,
  metadata: FieldMetadata,
  fieldKey: string
): React.ReactNode {
  if (value == null) {
    return null
  }

  // Status fields render as tags
  if (isStatusField(fieldKey, metadata) && typeof value === 'string') {
    const color = getStatusBadgeColor(value)
    const tagColors: Record<string, string> = {
      success: 'green',
      processing: 'blue',
      error: 'red',
      warning: 'orange',
      default: 'default',
    }
    return <Tag color={tagColors[color]}>{value}</Tag>
  }

  // Format other values
  return formatCellValue(value, metadata)
}

/**
 * MetadataTableCardView - Mobile card list view
 */
export function MetadataTableCardView({
  data,
  fields,
  displayColumns,
  loading = false,
  onCardClick,
  emptyMessage = 'No records found',
  total,
  returned,
  hasMore,
}: MetadataTableCardViewProps) {
  // Loading state
  if (loading) {
    return (
      <div style={{ padding: '16px' }}>
        {[1, 2, 3, 4, 5].map((i) => (
          <Card key={i} style={{ marginBottom: 12 }}>
            <Skeleton active paragraph={{ rows: 2 }} />
          </Card>
        ))}
      </div>
    )
  }

  // Empty state
  if (!data || data.length === 0) {
    return (
      <Empty
        description={emptyMessage}
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        style={{ padding: '48px 16px' }}
      />
    )
  }

  // Get primary field (first column or 'name')
  const primaryField = displayColumns[0] || 'name'
  const secondaryFields = displayColumns.slice(1, 4)

  // Build pagination info badge
  const showPaginationBadge = (total !== undefined && total > 0) || hasMore
  const paginationText = total !== undefined
    ? `Showing ${returned ?? data.length} of ${total.toLocaleString()} items${hasMore ? ' (more available)' : ''}`
    : hasMore
      ? `Showing ${returned ?? data.length} items (more available)`
      : null

  return (
    <div className="metadata-card-view-container">
      {/* Pagination info badge */}
      {showPaginationBadge && paginationText && (
        <div style={{ padding: '8px 16px', textAlign: 'center' }}>
          <Badge
            status={hasMore ? 'processing' : 'default'}
            text={<Text type="secondary" style={{ fontSize: 12 }}>{paginationText}</Text>}
          />
        </div>
      )}

      <List
        className="metadata-card-list"
        dataSource={data}
        renderItem={(record) => {
        const primaryValue = record[primaryField]
        const primaryMeta = fields[primaryField]

        return (
          <Card
            className="metadata-card"
            style={{ marginBottom: 12 }}
            onClick={() => onCardClick?.(record)}
            hoverable={!!onCardClick}
          >
            <div className="metadata-card-content">
              {/* Primary field (large) */}
              <div className="metadata-card-title">
                <Text strong style={{ fontSize: 16 }}>
                  {primaryMeta
                    ? formatCellValue(primaryValue, primaryMeta)
                    : String(primaryValue ?? '')}
                </Text>
              </div>

              {/* Secondary fields */}
              <div className="metadata-card-details">
                {secondaryFields.map((fieldKey) => {
                  const value = record[fieldKey]
                  const metadata = fields[fieldKey]
                  if (!metadata || value == null) return null

                  return (
                    <div key={fieldKey} className="metadata-card-field">
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {metadata.title ?? fieldKey}:
                      </Text>{' '}
                      <span>{renderCardValue(value, metadata, fieldKey)}</span>
                    </div>
                  )
                })}
              </div>

              {/* Chevron indicator */}
              {onCardClick && (
                <div className="metadata-card-arrow">
                  <RightOutlined style={{ color: '#999' }} />
                </div>
              )}
            </div>
          </Card>
        )
        }}
      />
    </div>
  )
}

export default MetadataTableCardView
